/**
 * Cliente para el nuevo backend Flask con Claude API
 * Reemplaza las llamadas a AWS Bedrock
 */

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API || 'http://localhost:5000';

/**
 * Llama al endpoint /query-stream del backend con streaming
 * @param {string} query - La pregunta del usuario
 * @param {string} username - Email o username del usuario
 * @param {Array} conversationHistory - Historial de mensajes anteriores
 * @param {Object} callbacks - Callbacks para eventos del stream
 * @param {string} agentType - Tipo de agente: 'subnacional' o 'nacional'
 * @param {Object} fileData - Datos del archivo adjunto (opcional)
 */
export const streamQuery = async (
    query,
    username,
    conversationHistory = [],
    callbacks = {},
    agentType = 'subnacional',
    fileData = null
) => {
    const {
        onChunk = () => {},
        onThinking = () => {},  // 🆕 Callback para thinking messages
        onToolUse = () => {},
        onComplete = () => {},
        onError = () => {}
    } = callbacks;

    try {
        console.log('[BACKEND] 🚀 Llamando a backend Flask:', BACKEND_API_URL);
        console.log('[BACKEND] 🎯 Tipo de agente:', agentType);

        // Construir payload
        const payload = {
            query,
            username,
            conversation_history: conversationHistory,
            agent_type: agentType
        };

        // 🆕 Agregar archivo si existe
        if (fileData) {
            console.log('[BACKEND] 📁 Adjuntando archivo:', fileData.filename, '| Tipo:', fileData.type);

            switch (fileData.type) {
                case 'image':
                    payload.image_data = fileData.base64;
                    payload.image_mime_type = fileData.mimeType;
                    payload.image_filename = fileData.filename;
                    break;
                case 'excel':
                    payload.excel_data = fileData.base64;
                    payload.excel_filename = fileData.filename;
                    break;
                case 'pdf':
                    payload.pdf_data = fileData.base64;
                    payload.pdf_filename = fileData.filename;
                    break;
            }
        }

        const response = await fetch(`${BACKEND_API_URL}/query-stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Leer el stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedText = '';

        while (true) {
            const { done, value } = await reader.read();

            if (done) {
                console.log('[BACKEND] ✅ Stream completado');
                break;
            }

            const text = decoder.decode(value, { stream: true });
            const lines = text.split('\n');

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6));

                        if (data.type === 'start') {
                            console.log('[BACKEND] 📡 Iniciando stream...');
                        }
                        else if (data.type === 'thinking_start') {
                            console.log('[BACKEND] 💭 Thinking iniciado...');
                            onThinking('start', '');
                        }
                        else if (data.type === 'thinking') {
                            // Pasar el thinking acumulado
                            onThinking('chunk', data.accumulated || data.content);
                        }
                        else if (data.type === 'thinking_stop') {
                            console.log('[BACKEND] 💭 Thinking completado');
                            onThinking('stop', '');
                        }
                        else if (data.type === 'chunk') {
                            accumulatedText += data.content;
                            onChunk(data.content, accumulatedText);
                        }
                        else if (data.type === 'tool_use') {
                            console.log('[BACKEND] 🔧 Tool usado:', data.tool);
                            onToolUse(data.tool);
                        }
                        else if (data.type === 'done') {
                            console.log('[BACKEND] ✅ Respuesta completa');
                            onComplete(data.content || accumulatedText);
                        }
                        else if (data.type === 'error') {
                            console.error('[BACKEND] ❌ Error:', data.error);
                            onError(new Error(data.error));
                        }
                    } catch (parseError) {
                        console.warn('[BACKEND] ⚠️ Error parseando JSON:', parseError);
                    }
                }
            }
        }

        return accumulatedText;

    } catch (error) {
        console.error('[BACKEND] ❌ Error en streamQuery:', error);
        onError(error);
        throw error;
    }
};

/**
 * Llama al endpoint /health para verificar estado del backend
 */
export const checkBackendHealth = async () => {
    try {
        const response = await fetch(`${BACKEND_API_URL}/health`);
        const data = await response.json();
        console.log('[BACKEND] 💚 Health check:', data);
        return data;
    } catch (error) {
        console.error('[BACKEND] ❌ Error en health check:', error);
        return { status: 'error', error: error.message };
    }
};

/**
 * Obtiene información del servicio
 */
export const getBackendInfo = async () => {
    try {
        const response = await fetch(`${BACKEND_API_URL}/`);
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('[BACKEND] ❌ Error obteniendo info:', error);
        return null;
    }
};
